import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Calendar, Clock, CheckCircle, XCircle, AlertCircle, Loader2, Wallet } from "lucide-react";

export default function LeaveManagement({ user }) {
  const [balance, setBalance] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balanceError, setBalanceError] = useState(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [maternityDialogOpen, setMaternityDialogOpen] = useState(false);
  const [encashDialogOpen, setEncashDialogOpen] = useState(false);
  const [encashEligibility, setEncashEligibility] = useState(null);
  const [encashRequests, setEncashRequests] = useState([]);
  const [encashForm, setEncashForm] = useState({ days: "", reason: "" });
  const [encashSubmitting, setEncashSubmitting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Cancellation request state
  const [cancelRequestDialog, setCancelRequestDialog] = useState(false);
  const [cancelTargetApp, setCancelTargetApp] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const [formData, setFormData] = useState({
    leave_type: "casual",
    start_date: "",
    end_date: "",
    reason: "",
    is_half_day: false,
    half_day_period: "AM"
  });
  const [employmentStatus, setEmploymentStatus] = useState("Current");

  const [maternityData, setMaternityData] = useState({
    expected_delivery_date: "",
    start_date: "",
    end_date: "",
    reason: "",
    medical_certificate: "",
    emergency_contact: ""
  });

  useEffect(() => {
    fetchLeaveBalance();
    fetchMyApplications();
    fetchEmploymentStatus();
    fetchEncashmentData();
  }, []);

  const fetchEncashmentData = async () => {
    try {
      const [elig, reqs] = await Promise.all([
        api.get("/leave/el-encashment-requests/eligibility").catch(() => ({ data: null })),
        api.get("/leave/el-encashment-requests/my").catch(() => ({ data: [] }))
      ]);
      setEncashEligibility(elig.data);
      setEncashRequests(reqs.data || []);
    } catch (e) {
      // Silently fail; section will hide
    }
  };

  const handleApplyEncashment = async (e) => {
    e.preventDefault();
    setEncashSubmitting(true);
    try {
      const payload = {
        days: parseFloat(encashForm.days),
        reason: encashForm.reason.trim()
      };
      await api.post("/leave/el-encashment-requests", payload);
      toast.success("Encashment request submitted for HR approval");
      setEncashDialogOpen(false);
      setEncashForm({ days: "", reason: "" });
      fetchEncashmentData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit encashment request");
    } finally {
      setEncashSubmitting(false);
    }
  };

  const handleCancelEncashment = async (requestId) => {
    if (!confirm("Cancel this pending encashment request?")) return;
    try {
      await api.delete(`/leave/el-encashment-requests/${requestId}`);
      toast.success("Encashment request cancelled");
      fetchEncashmentData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel request");
    }
  };

  const fetchEmploymentStatus = async () => {
    try {
      // Fetch current user's employment status from employee record
      // This requires knowing the employee_id — we'll get it from the balance endpoint indirectly
      // For now, use a simple fetch of "my" employee data
      const res = await api.get("/employees/me-status").catch(() => null);
      if (res?.data?.employment_status) {
        setEmploymentStatus(res.data.employment_status);
      }
    } catch (e) {
      // Silently fail — default is Current
    }
  };

  const fetchLeaveBalance = async () => {
    try {
      const response = await api.get("/leave/balance");
      setBalance(response.data);
      setBalanceError(null);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to fetch leave balance";
      toast.error(errorMessage);
      setBalanceError(errorMessage);
      console.error("Leave balance error:", error);
    }
  };

  const fetchMyApplications = async () => {
    try {
      setLoading(true);
      const response = await api.get("/leave/applications/my");
      setApplications(response.data);
    } catch (error) {
      toast.error("Failed to fetch leave applications");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    
    if (!formData.start_date || !formData.end_date || !formData.reason) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSubmitting(true);
      await api.post("/leave/applications", formData);
      toast.success("Leave application submitted successfully");
      setApplyDialogOpen(false);
      resetForm();
      fetchLeaveBalance();
      fetchMyApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply for leave");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyMaternity = async (e) => {
    e.preventDefault();
    
    if (!maternityData.expected_delivery_date || !maternityData.start_date || !maternityData.end_date) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSubmitting(true);
      await api.post("/leave/maternity", maternityData);
      toast.success("Maternity leave application submitted successfully");
      setMaternityDialogOpen(false);
      resetMaternityForm();
      fetchLeaveBalance();
      fetchMyApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply for maternity leave");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelLeave = async (applicationId) => {
    if (!confirm("Are you sure you want to cancel this leave application?")) {
      return;
    }

    try {
      await api.put(`/leave/applications/${applicationId}/cancel`);
      toast.success("Leave application cancelled successfully");
      fetchMyApplications();
      fetchLeaveBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel leave");
    }
  };

  const openCancelRequestDialog = (app) => {
    setCancelTargetApp(app);
    setCancelReason("");
    setCancelRequestDialog(true);
  };

  const submitCancellationRequest = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a cancellation reason");
      return;
    }
    try {
      await api.put(`/leave/applications/${cancelTargetApp.id}/cancel-request`, {
        cancellation_reason: cancelReason.trim()
      });
      toast.success("Cancellation request submitted for manager approval");
      setCancelRequestDialog(false);
      fetchMyApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request cancellation");
    }
  };

  // Helper: an approved leave is eligible for cancellation request if start_date > today and not LOP
  const isFutureApproved = (app) => {
    if (app.status !== "approved" || app.is_lop) return false;
    try {
      const start = new Date(app.start_date + "T00:00:00");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return start > today;
    } catch {
      return false;
    }
  };

  const resetForm = () => {
    setFormData({
      leave_type: "casual",
      start_date: "",
      end_date: "",
      reason: "",
      is_half_day: false,
      half_day_period: "AM"
    });
  };

  const resetMaternityForm = () => {
    setMaternityData({
      expected_delivery_date: "",
      start_date: "",
      end_date: "",
      reason: "",
      medical_certificate: "",
      emergency_contact: ""
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock, label: "Pending" },
      approved: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "Approved" },
      rejected: { color: "bg-red-100 text-red-800", icon: XCircle, label: "Rejected" },
      cancelled: { color: "bg-md3-surface-container text-md3-on-surface", icon: AlertCircle, label: "Cancelled" },
      cancellation_requested: { color: "bg-orange-100 text-orange-800", icon: Clock, label: "Cancel Pending" }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getLeaveTypeColor = (type) => {
    const colors = {
      casual: "text-blue-600",
      sick: "text-red-600",
      earned: "text-green-600",
      maternity: "text-orange-600",
      lop: "text-red-800"
    };
    return colors[type] || "text-md3-on-surface-variant";
  };

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small">My Leaves</h1>
            <p className="text-md3-on-surface-variant">Manage your leave applications and balance</p>
          </div>
          <div className="flex gap-2">
            {encashEligibility && user?.userType !== "Contract" && (encashEligibility.eligible || encashEligibility.has_pending_request) && (
              <Button onClick={() => setEncashDialogOpen(true)} variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50 transition-all duration-200 rounded-md3-full" disabled={!encashEligibility.eligible} data-testid="apply-encashment-btn">
                <Wallet className="h-4 w-4 mr-2" />
                {encashEligibility.has_pending_request ? "Encashment Pending" : "Apply for Encashment"}
              </Button>
            )}
            {balance?.maternity && !balanceError && (
              <Button onClick={() => setMaternityDialogOpen(true)} variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" disabled={!!balanceError}>
                <Plus className="h-4 w-4 mr-2" />
                Apply Maternity Leave
              </Button>
            )}
            <Button onClick={() => setApplyDialogOpen(true)} className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" disabled={!!balanceError}>
              <Plus className="h-4 w-4 mr-2" />
              Apply Leave
            </Button>
          </div>
        </div>

        {/* Leave Balance Cards or Error Message */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {balanceError ? (
            <div className="col-span-4">
              <Card className="border-red-200 bg-red-50 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-red-100 rounded-full">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-red-900">Leave Management Not Available</h3>
                      <p className="text-md3-body-small text-red-700 mt-1">{balanceError}</p>
                      <p className="text-xs text-red-600 mt-2">
                        Please contact your HR administrator to set up your employee profile.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : balance ? (
            <>
              <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                <CardHeader className="pb-3">
                  <CardTitle className="text-md3-body-small font-medium text-blue-600 ">Casual Leave</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-md3-display-small">{balance.casual?.remaining || 0}</div>
                  <p className="text-xs text-md3-on-surface-variant">
                    of {balance.casual?.total || 0} days available
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                <CardHeader className="pb-3">
                  <CardTitle className="text-md3-body-small font-medium text-red-600 ">Sick Leave</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-md3-display-small">{balance.sick?.remaining || 0}</div>
                  <p className="text-xs text-md3-on-surface-variant">
                    of {balance.sick?.total || 0} days available
                  </p>
                </CardContent>
              </Card>

              {user?.userType !== "Contract" && (
              <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                <CardHeader className="pb-3">
                  <CardTitle className="text-md3-body-small font-medium text-green-600 ">Earned Leave</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-md3-display-small">{balance.earned?.remaining || 0}</div>
                  <p className="text-xs text-md3-on-surface-variant">
                    of {balance.earned?.total || 0} days available
                  </p>
                  {balance.carry_forward_earned > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      +{balance.carry_forward_earned} carried forward
                    </p>
                  )}
                </CardContent>
              </Card>
              )}

              {balance.maternity && (
                <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-md3-body-small font-medium text-orange-600 ">Maternity Leave</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-md3-display-small">{balance.maternity?.remaining || 0}</div>
                    <p className="text-xs text-md3-on-surface-variant">
                      of {balance.maternity?.total || 0} days available
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <div className="col-span-4">
              <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                <CardContent className="pt-6 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400 mb-2" />
                  <p className="text-md3-on-surface-variant">Loading leave balance...</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Leave Applications Table */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface ">Leave History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-md3-on-surface-variant">No leave applications found</p>
              </div>
            ) : (
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>Application #</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.id}>
                      <TableCell className="font-medium">{app.application_number}</TableCell>
                      <TableCell>
                        <span className={`font-medium ${getLeaveTypeColor(app.leave_type)}`}>
                          {app.leave_type.charAt(0).toUpperCase() + app.leave_type.slice(1)}
                          {app.is_half_day && <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{app.half_day_period || "Half"}</span>}
                          {app.is_lop && <span className="ml-1 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">LOP</span>}
                        </span>
                      </TableCell>
                      <TableCell>{app.start_date}</TableCell>
                      <TableCell>{app.end_date}</TableCell>
                      <TableCell>{app.total_days}</TableCell>
                      <TableCell>{getStatusBadge(app.status)}</TableCell>
                      <TableCell>
                        {app.status === "pending" && (
                          <Button size="sm"
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                            onClick={() => handleCancelLeave(app.id)}>
                            Cancel
                          </Button>
                        )}
                        {isFutureApproved(app) && (
                          <Button size="sm"
                            variant="outline"
                            className="border-orange-400 text-orange-700 hover:bg-orange-50 rounded-md3-full"
                            onClick={() => openCancelRequestDialog(app)}
                            data-testid={`cancel-request-btn-${app.id}`}>
                            Request Cancel
                          </Button>
                        )}
                        {app.status === "cancellation_requested" && (
                          <Badge className="bg-orange-100 text-orange-800">Awaiting Manager</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* My Encashment Requests */}
        {encashRequests && encashRequests.length > 0 && (
          <Card className="shadow-md3-1 rounded-md3-lg border-0 bg-md3-surface-container">
            <CardHeader>
              <CardTitle className="text-md3-title-medium flex items-center gap-2">
                <Wallet className="h-4 w-4 text-amber-600" /> My Encashment Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Applied</TableHead>
                    <TableHead className="text-right">Days</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>HR Note</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {encashRequests.map((r) => (
                    <TableRow key={r.id} data-testid={`encash-row-${r.id}`}>
                      <TableCell className="text-sm">{r.applied_at ? new Date(r.applied_at).toLocaleDateString() : "-"}</TableCell>
                      <TableCell className="text-right font-semibold">{r.days}</TableCell>
                      <TableCell className="text-sm">{r.reason}</TableCell>
                      <TableCell>
                        <Badge className={
                          r.status === "approved" ? "bg-green-100 text-green-800" :
                          r.status === "rejected" ? "bg-red-100 text-red-800" :
                          "bg-amber-100 text-amber-800"
                        }>{r.status}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-md3-on-surface-variant">
                        {r.status === "rejected" ? r.rejection_reason : (r.status === "approved" ? `by ${r.approved_by}` : "—")}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.status === "pending" && (
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleCancelEncashment(r.id)} data-testid={`encash-cancel-${r.id}`}>
                            Cancel
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Apply Leave Dialog */}
        <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Apply for Leave</DialogTitle>
            </DialogHeader>
            {employmentStatus === "Resigned" && (
              <div className="px-6 py-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                You are currently on notice period. Only Sick Leave is available.
              </div>
            )}
            <form onSubmit={handleApplyLeave} className="space-y-6 p-6 pt-2">
              <div className="space-y-2">
                <Label htmlFor="leave_type" className="text-md3-label-large text-gray-700 font-medium">Leave Type *</Label>
                <Select
                  value={formData.leave_type}
                  onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                >
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" data-testid="leave-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {employmentStatus !== "Resigned" && <SelectItem value="casual">Casual Leave</SelectItem>}
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    {employmentStatus !== "Resigned" && user?.userType !== "Contract" && <SelectItem value="earned">Earned Leave</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              {/* Half-day toggle */}
              <div className="flex items-center gap-3 p-3 border rounded-md3-lg bg-gray-50">
                <input
                  type="checkbox"
                  id="is_half_day"
                  checked={formData.is_half_day}
                  onChange={(e) => {
                    const halfDay = e.target.checked;
                    setFormData({ ...formData, is_half_day: halfDay, end_date: halfDay ? formData.start_date : formData.end_date });
                  }}
                  className="h-4 w-4"
                  data-testid="half-day-checkbox"
                />
                <Label htmlFor="is_half_day" className="cursor-pointer font-medium">Half Day</Label>
                {formData.is_half_day && (
                  <Select
                    value={formData.half_day_period}
                    onValueChange={(v) => setFormData({ ...formData, half_day_period: v })}
                  >
                    <SelectTrigger className="w-24 h-8 text-sm bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="start_date" className="text-md3-label-large text-gray-700 font-medium">Start Date *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setFormData({ ...formData, start_date: newDate, end_date: formData.is_half_day ? newDate : formData.end_date });
                  }}
                  required
                />
              </div>

              {!formData.is_half_day && (
                <div className="space-y-2">
                  <Label htmlFor="end_date" className="text-md3-label-large text-gray-700 font-medium">End Date *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                  />
                </div>
              )}
              {formData.is_half_day && (
                <p className="text-xs text-gray-500">For half-day, end date is automatically set to the same day.</p>
              )}

              <div className="space-y-2">
                <Label htmlFor="reason" className="text-md3-label-large text-gray-700 font-medium">Reason *</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Enter reason for leave"
                  rows={3}
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setApplyDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Application"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Apply Maternity Leave Dialog */}
        <Dialog open={maternityDialogOpen} onOpenChange={setMaternityDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Apply for Maternity Leave</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleApplyMaternity} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="expected_delivery_date" className="text-md3-label-large text-gray-700 font-medium">Expected Delivery Date *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="expected_delivery_date"
                  type="date"
                  value={maternityData.expected_delivery_date}
                  onChange={(e) => setMaternityData({ ...maternityData, expected_delivery_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mat_start_date" className="text-md3-label-large text-gray-700 font-medium">Leave Start Date *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="mat_start_date"
                  type="date"
                  value={maternityData.start_date}
                  onChange={(e) => setMaternityData({ ...maternityData, start_date: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mat_end_date" className="text-md3-label-large text-gray-700 font-medium">Leave End Date *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="mat_end_date"
                  type="date"
                  value={maternityData.end_date}
                  onChange={(e) => setMaternityData({ ...maternityData, end_date: e.target.value })}
                  required
                />
                <p className="text-xs text-md3-on-surface-variant">Maximum 180 days</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="medical_certificate" className="text-md3-label-large text-gray-700 font-medium">Medical Certificate</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="medical_certificate"
                  type="text"
                  value={maternityData.medical_certificate}
                  onChange={(e) => setMaternityData({ ...maternityData, medical_certificate: e.target.value })}
                  placeholder="Upload link or reference"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="emergency_contact" className="text-md3-label-large text-gray-700 font-medium">Emergency Contact</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="emergency_contact"
                  type="text"
                  value={maternityData.emergency_contact}
                  onChange={(e) => setMaternityData({ ...maternityData, emergency_contact: e.target.value })}
                  placeholder="Emergency contact number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mat_reason" className="text-md3-label-large text-gray-700 font-medium">Additional Notes</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="mat_reason"
                  value={maternityData.reason}
                  onChange={(e) => setMaternityData({ ...maternityData, reason: e.target.value })}
                  placeholder="Any additional information"
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setMaternityDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Application"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Cancellation Request Dialog */}
        <Dialog open={cancelRequestDialog} onOpenChange={setCancelRequestDialog}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small">Request Leave Cancellation</DialogTitle>
            </DialogHeader>
            {cancelTargetApp && (
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm">
                  <p><strong>{cancelTargetApp.leave_type?.charAt(0).toUpperCase() + cancelTargetApp.leave_type?.slice(1)} Leave</strong></p>
                  <p>{cancelTargetApp.start_date} to {cancelTargetApp.end_date} · {cancelTargetApp.total_days} day(s)</p>
                  <p className="text-xs text-md3-on-surface-variant mt-1">Your manager must approve this request. Until then, the leave remains active.</p>
                </div>
                <div>
                  <Label>Reason for cancellation *</Label>
                  <Textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows={3}
                    placeholder="Why do you need to cancel this leave?"
                    data-testid="cancel-reason-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setCancelRequestDialog(false)}>Close</Button>
                  <Button onClick={submitCancellationRequest} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="cancel-request-submit-btn">
                    Submit Cancellation Request
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Apply for EL Encashment Dialog */}
        <Dialog open={encashDialogOpen} onOpenChange={setEncashDialogOpen}>
          <DialogContent className="max-w-md bg-md3-surface-container-high">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small flex items-center gap-2">
                <Wallet className="h-5 w-5 text-amber-600" /> Apply for EL Encashment
              </DialogTitle>
            </DialogHeader>
            {encashEligibility && (
              <div className="rounded-md3-md bg-amber-50 border border-amber-200 p-3 text-sm">
                <div className="flex justify-between"><span>EL balance</span><span className="font-semibold">{encashEligibility.el_balance} days</span></div>
                <div className="flex justify-between"><span>Threshold</span><span className="font-semibold">{encashEligibility.threshold} days</span></div>
                <div className="flex justify-between"><span>Encashable</span><span className="font-semibold text-amber-700">{encashEligibility.encashable_days} days</span></div>
                <div className="flex justify-between"><span>Max per request</span><span className="font-semibold">{encashEligibility.max_per_request} days</span></div>
              </div>
            )}
            <form onSubmit={handleApplyEncashment} className="space-y-4">
              <div>
                <Label>Days to Encash *</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max={Math.min(encashEligibility?.encashable_days || 0, encashEligibility?.max_per_request || 0)}
                  value={encashForm.days}
                  onChange={(e) => setEncashForm({ ...encashForm, days: e.target.value })}
                  required
                  data-testid="encash-days-input"
                />
              </div>
              <div>
                <Label>Reason *</Label>
                <Textarea
                  value={encashForm.reason}
                  onChange={(e) => setEncashForm({ ...encashForm, reason: e.target.value })}
                  rows={3}
                  required
                  placeholder="Why do you want to encash these days?"
                  data-testid="encash-reason-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEncashDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={encashSubmitting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="encash-submit-btn">
                  {encashSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting</> : "Submit Request"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
